import api from "services/api";

const unwrap = (response) => response?.data?.data ?? response?.data ?? [];
const unwrapList = (response) => {
  const value = unwrap(response);
  return Array.isArray(value) ? value : [];
};
const getDocumentId = (document) => document?.documentId ?? document?.id;
const byDisplayOrder = (left, right) =>
  Number(left?.displayOrder ?? left?.DisplayOrder ?? 0) - Number(right?.displayOrder ?? right?.DisplayOrder ?? 0);

const buildTeamMemberPayload = (data = {}) => {
  const payload = {
    ...data,
    subcontractId: data.subcontractId ?? data.SubcontractId,
    userId: data.userId ?? data.UserId ?? data.personnelId ?? data.PersonnelId,
    roleInSubcontract: data.roleInSubcontract ?? data.RoleInSubcontract ?? "Thành viên",
    joinDate: data.joinDate ?? data.JoinDate,
    displayOrder: data.displayOrder ?? data.DisplayOrder ?? 0,
  };

  if (!payload.subcontractId || !payload.userId || !payload.joinDate) {
    throw new Error("Thiếu thông tin bắt buộc khi cập nhật nhân sự giao khoán.");
  }

  return payload;
};

const getItemCodeParts = (code) =>
  String(code || "")
    .split(".")
    .map((part) => part.trim())
    .filter(Boolean);

const compareItemCodeParts = (leftPart = "", rightPart = "") => {
  const leftIsNumber = /^\d+$/.test(leftPart);
  const rightIsNumber = /^\d+$/.test(rightPart);

  if (leftIsNumber && rightIsNumber) {
    return Number(leftPart) - Number(rightPart);
  }

  return leftPart.localeCompare(rightPart, "vi", {
    numeric: true,
    sensitivity: "base",
  });
};

const compareItemCode = (left, right) => {
  const leftParts = getItemCodeParts(left?.itemCode ?? left?.ItemCode);
  const rightParts = getItemCodeParts(right?.itemCode ?? right?.ItemCode);
  const maxLength = Math.max(leftParts.length, rightParts.length);

  for (let index = 0; index < maxLength; index += 1) {
    const comparison = compareItemCodeParts(leftParts[index], rightParts[index]);
    if (comparison !== 0) return comparison;
  }

  return Number(left?.itemId ?? left?.ItemId ?? 0) - Number(right?.itemId ?? right?.ItemId ?? 0);
};

const sortItemsByCode = (items = []) => [...items].sort(compareItemCode);

const getFileNameFromDisposition = (disposition) => {
  if (!disposition) return null;
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);
  const match = disposition.match(/filename="?([^"]+)"?/i);
  return match?.[1] || null;
};

const attachItems = async (profiles, loadItems) =>
  Promise.all(
    profiles.map(async (profile) => ({
      ...profile,
      items: await loadItems(profile),
    })),
  );

export const subcontractApi = {
  async getSubcontracts(params = {}) {
    const response = await api.get("/Subcontract", { params });
    return {
      data: response?.data?.data ?? [],
      totalCount: response?.data?.totalCount ?? 0,
    };
  },
  async getSubcontract(id) {
    const response = await api.get(`/Subcontract/${id}`);
    return response?.data?.data ?? response?.data;
  },
  async createSubcontract(data) {
    const response = await api.post("/Subcontract", data);
    return response?.data?.data ?? response?.data;
  },
  async updateSubcontract(id, data) {
    const response = await api.put(`/Subcontract/${id}`, data);
    return response?.data?.data ?? response?.data;
  },
  async deleteSubcontract(id, changeReason = "Hủy bỏ quyết định giao khoán") {
    const response = await api.delete(`/Subcontract/${id}`, {
      params: { changeReason },
    });
    return response?.data?.data ?? response?.data;
  },
  async getContracts(params = {}) {
    const response = await api.get("/Contract", {
      params: { pageSize: 1000, pageNumber: 1, ...params },
    });
    return {
      data: response?.data?.data ?? [],
      totalCount: response?.data?.totalCount ?? 0,
    };
  },
  async getMainContract(id) {
    const response = await api.get(`/Contract/${id}`);
    return response?.data?.data ?? response?.data;
  },
  async getMainContractPaymentProfiles(id) {
    return unwrapList(await api.get(`/PaymentProfile/contract/${id}`));
  },
  async getMainContractPayments(id) {
    return unwrapList(await api.get(`/ContractPayment/contract/${id}`));
  },
  async getSubcontractors(params = {}) {
    const response = await api.get("/Subcontractor", {
      params: {
        pageSize: 1000,
        pageNumber: 1,
        sortColumn: "SubcontractorName",
        sortDirection: "ASC",
        ...params,
      },
    });
    return {
      data: response?.data?.data ?? [],
      totalCount: response?.data?.totalCount ?? 0,
    };
  },
  async getSubcontractGroups(params = {}) {
    return unwrapList(await api.get("/ListSubcontractGroup", {
      params: { pageSize: 1000, pageNumber: 1, sortColumn: "Name", sortDirection: "ASC", ...params },
    }));
  },
  async getExecutionGroups(params = {}) {
    return unwrapList(await api.get("/SubcontractExecutionGroup", {
      params: { pageSize: 1000, pageNumber: 1, ...params },
    }));
  },
  async getDepartments(params = {}) {
    return unwrapList(await api.get("/Department", {
      params: { pageSize: 1000, pageNumber: 1, ...params },
    }));
  },
  async getDepartmentUnits(params = {}) {
    return unwrapList(await api.get("/DepartmentUnit", {
      params: { pageSize: 1000, pageNumber: 1, ...params },
    }));
  },
  async getSubcontractAssignee(subcontractId) {
    const response = await api.get(`/SubcontractAssignee/subcontract/${subcontractId}`);
    return response?.data?.data ?? response?.data;
  },
  async saveSubcontractAssignee(data) {
    const response = await api.post("/SubcontractAssignee/save-from-source", data);
    return response?.data?.data ?? response?.data;
  },
  async getProvinces(params = {}) {
    const response = await api.get("/Province", {
      params: { pageSize: 1000, pageNumber: 1, ...params },
    });
    return {
      data: response?.data?.data ?? [],
      totalCount: response?.data?.totalCount ?? 0,
    };
  },
  async getCommunes(params = {}) {
    const response = await api.get("/Commune", {
      params: { pageSize: 1000, pageNumber: 1, ...params },
    });
    return {
      data: response?.data?.data ?? [],
      totalCount: response?.data?.totalCount ?? 0,
    };
  },
  async getApprovalProfiles(params = {}) {
    const response = await api.get("/SubcontractApprovalProfile", { params });
    return {
      data: response?.data?.data ?? [],
      totalCount: response?.data?.totalCount ?? 0,
    };
  },
  async getApprovalProfilesBySubcontract(id) {
    const profiles = unwrapList(await api.get(`/SubcontractApprovalProfile/subcontract/${id}`));
    return attachItems(profiles, async (profile) =>
      unwrapList(await api.get(`/SubcontractApprovalItem/approval-profile/${profile.approvalProfileId}`)),
    );
  },
  async createApprovalProfile(data) {
    const response = await api.post("/SubcontractApprovalProfile", data);
    return response?.data?.data ?? response?.data;
  },
  async updateApprovalProfile(id, data) {
    const statusText = String(data?.status || data?.Status || "").toLowerCase();
    const isStepDecision =
      Boolean(data?.respondedAt || data?.RespondedAt) &&
      (statusText.includes("duy") || statusText.includes("ph") || statusText.includes("sửa") || statusText.includes("sua"));

    if (isStepDecision) {
      const isRevision = statusText.includes("sửa") || statusText.includes("sua");
      const response = await api.post(`/SubcontractApprovalProfile/${id}/approve-step`, {
        action: isRevision ? "revision" : "approve",
        comment: data?.overallComment ?? data?.OverallComment ?? null,
      });
      return response?.data?.data ?? response?.data;
    }

    const response = await api.put(`/SubcontractApprovalProfile/${id}`, data);
    return response?.data?.data ?? response?.data;
  },
  async approveApprovalStep(id, data) {
    const response = await api.post(`/SubcontractApprovalProfile/${id}/approve-step`, data);
    return response?.data?.data ?? response?.data;
  },
  async reviewApprovalProfile(id, data) {
    const response = await api.post(`/SubcontractApprovalProfile/${id}/review`, data);
    return response?.data?.data ?? response?.data;
  },
  async deleteApprovalProfile(id) {
    const response = await api.delete(`/SubcontractApprovalProfile/${id}`);
    return response?.data;
  },
  async createApprovalItem(data) {
    const response = await api.post("/SubcontractApprovalItem", data);
    return response?.data?.data ?? response?.data;
  },
  async updateApprovalItem(id, data) {
    const response = await api.put(`/SubcontractApprovalItem/${id}`, data);
    return response?.data?.data ?? response?.data;
  },
  async getTeam(id) {
    const members = unwrapList(await api.get(`/SubcontractTeam/subcontract/${id}`, { params: { pageSize: 500 } }));
    return members.map((member) => ({
      ...member,
      personnelId: member.personnelId ?? member.PersonnelId ?? member.userId ?? member.UserId,
      teamMemberId: member.teamMemberId ?? member.subcontractTeamId ?? member.SubcontractTeamId,
    }));
  },
  async getPersonnel(params = {}) {
    const response = await api.get("/Personnel", {
      params: {
        pageSize: 1000,
        pageNumber: 1,
        sortColumn: "displayOrder",
        sortDirection: "ASC",
        isActive: true,
        ...params,
      },
    });
    return {
      data: response?.data?.data ?? [],
      totalCount: response?.data?.totalCount ?? 0,
    };
  },
  async getRoleOptions() {
    return unwrapList(await api.get("/RoleInSubcontract", { params: { pageSize: 1000, pageNumber: 1 } }));
  },
  async addTeamMember(data) {
    const response = await api.post("/SubcontractTeam", buildTeamMemberPayload(data));
    return response?.data?.data ?? response?.data;
  },
  async updateTeamMember(id, data) {
    const response = await api.put(`/SubcontractTeam/${id}`, buildTeamMemberPayload(data));
    return response?.data?.data ?? response?.data;
  },
  async removeTeamMember(id) {
    const response = await api.delete(`/SubcontractTeam/${id}`);
    return response?.data;
  },
  async getDocuments(id) {
    return unwrap(await api.get(`/subcontract-documents/by-subcontract/${id}`, { params: { pageSize: 500 } }));
  },
  async getDocumentTypes(params = {}) {
    const response = await api.get("/DocumentType", {
      params: {
        pageSize: 1000,
        pageNumber: 1,
        sortColumn: "DocumentCategory",
        sortDirection: "ASC",
        ...params,
      },
    });
    return unwrapList(response);
  },
  async uploadDocument({ subcontractId, file, documentType, description }) {
    const formData = new FormData();
    formData.append("SubcontractId", subcontractId);
    formData.append("File", file);
    if (documentType) formData.append("DocumentType", documentType);
    if (description) formData.append("Description", description);

    const response = await api.post("/subcontract-documents/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response?.data?.data ?? response?.data;
  },
  async updateDocument(id, data = {}) {
    const response = await api.put(`/subcontract-documents/${id}`, {
      documentName: data.documentName,
      documentType: data.documentType,
      description: data.description,
    });
    return response?.data?.data ?? response?.data;
  },
  async deleteDocument(id) {
    const response = await api.delete(`/subcontract-documents/${id}`);
    return response?.data;
  },
  async downloadDocument(id) {
    return api.get(`/subcontract-documents/download/${id}`, { responseType: "blob" });
  },
  async downloadDocumentFile(document) {
    const id = getDocumentId(document);
    if (!id) return;

    const response = await this.downloadDocument(id);
    const blob = new Blob([response.data], {
      type: response.headers["content-type"] || "application/octet-stream",
    });
    const fileName =
      getFileNameFromDisposition(response.headers["content-disposition"]) ||
      document.documentName ||
      document.fileName ||
      "document";
    const url = window.URL.createObjectURL(blob);
    const link = window.document.createElement("a");
    link.href = url;
    link.download = fileName;
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
  async getAssignmentDecisionDefaults(id) {
    const response = await api.get(`/Subcontract/${id}/assignment-decision/defaults`);
    return response?.data?.data ?? response?.data?.Data ?? response?.data;
  },
  async downloadAssignmentDecisionPdf(id, payload) {
    let response;
    try {
      response = await api.post(`/Subcontract/${id}/assignment-decision/pdf`, payload, {
        responseType: "blob",
      });
    } catch (error) {
      const blob = error?.response?.data;
      if (blob instanceof Blob) {
        const text = await blob.text();
        try {
          const parsed = JSON.parse(text);
          throw new Error(parsed?.detail || parsed?.Detail || parsed?.message || parsed?.Message || text);
        } catch (parseError) {
          if (parseError.message && parseError.message !== text) throw parseError;
          throw new Error(text || "Không thể tạo file PDF.");
        }
      }
      throw error;
    }
    const blob = new Blob([response.data], {
      type: response.headers["content-type"] || "application/pdf",
    });
    const fileName =
      getFileNameFromDisposition(response.headers["content-disposition"]) ||
      `Quyet-dinh-giao-khoan-${payload?.documentNumber || "QD"}.pdf`;
    const url = window.URL.createObjectURL(blob);
    const link = window.document.createElement("a");
    link.href = url;
    link.download = fileName;
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
  async downloadAssignmentDecisionWord(id, payload) {
    let response;
    try {
      response = await api.post(`/Subcontract/${id}/assignment-decision/word`, payload, {
        responseType: "blob",
      });
    } catch (error) {
      const blob = error?.response?.data;
      if (blob instanceof Blob) {
        const text = await blob.text();
        try {
          const parsed = JSON.parse(text);
          throw new Error(parsed?.detail || parsed?.Detail || parsed?.message || parsed?.Message || text);
        } catch (parseError) {
          if (parseError.message && parseError.message !== text) throw parseError;
          throw new Error(text || "Không thể tạo file Word.");
        }
      }
      throw error;
    }
    const blob = new Blob([response.data], {
      type: response.headers["content-type"] || "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });
    const fileName =
      getFileNameFromDisposition(response.headers["content-disposition"]) ||
      `Quyet-dinh-giao-khoan-${payload?.documentNumber || "QD"}.docx`;
    const url = window.URL.createObjectURL(blob);
    const link = window.document.createElement("a");
    link.href = url;
    link.download = fileName;
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
  async getSections(id) {
    const sections = unwrapList(
      await api.get("/SubcontractSection", { params: { pageSize: 500, subcontractId: id } }),
    ).sort(byDisplayOrder);
    const allItems = unwrapList(
      await api.get(`/subcontract-items/by-subcontract/${id}`, { params: { pageSize: 10000, pageNumber: 1 } }),
    );
    const itemsByScopeId = allItems.reduce((map, item) => {
      const scopeId = item.subcontractItemScopeId ?? item.SubcontractItemScopeId;
      if (!scopeId) return map;
      if (!map.has(scopeId)) map.set(scopeId, []);
      map.get(scopeId).push(item);
      return map;
    }, new Map());

    return Promise.all(
      sections.map(async (section) => {
        const scopes = unwrapList(
          await api.get("/SubcontractItemScope", {
            params: { pageSize: 500, sectionId: section.subcontractSectionId },
          }),
        ).sort(byDisplayOrder).map((scope) => ({
          ...scope,
          id: scope.subcontractItemScopeId,
          name: scope.scopeName,
          items: sortItemsByCode(itemsByScopeId.get(scope.subcontractItemScopeId) || []),
        }));

        return {
          ...section,
          id: section.subcontractSectionId,
          name: section.sectionName,
          scopes,
        };
      }),
    );
  },
  async getScopes(sectionId) {
    return unwrap(await api.get("/SubcontractItemScope", { params: { pageSize: 500, sectionId } }));
  },
  async createScope(data) {
    const response = await api.post("/SubcontractItemScope", data);
    return response?.data?.data ?? response?.data;
  },
  async getItems(id) {
    return sortItemsByCode(unwrap(await api.get(`/subcontract-items/by-subcontract/${id}`, { params: { pageSize: 10000, pageNumber: 1 } })));
  },
  async getDraftItems(id) {
    return sortItemsByCode(unwrap(await api.get(`/draf-subcontract-items/by-subcontract/${id}`)));
  },
  async createDraftItem(data) {
    const response = await api.post("/draf-subcontract-items", data);
    return response?.data?.data ?? response?.data;
  },
  async createDraftItemBatch(data) {
    const response = await api.post("/draf-subcontract-items/batch-create", data);
    return response?.data;
  },
  async updateDraftItem(id, data) {
    const response = await api.put(`/draf-subcontract-items/${id}`, data);
    return response?.data?.data ?? response?.data;
  },
  async deleteDraftItem(id) {
    const response = await api.delete(`/draf-subcontract-items/${id}`);
    return response?.data;
  },
  async getItemsByScope(scopeId) {
    return sortItemsByCode(unwrap(await api.get(`/subcontract-items/by-scope/${scopeId}`, { params: { pageSize: 10000, pageNumber: 1 } })));
  },
  async createItem(data) {
    const response = await api.post("/subcontract-items", data);
    return response?.data?.data ?? response?.data;
  },
  async importItems(data) {
    const response = await api.post("/subcontract-items/import", data);
    return response?.data;
  },
  async importItemsBatch(data) {
    const response = await api.post("/subcontract-items/import-batch", data, { timeout: 120000 });
    return response?.data;
  },
  async createItemBatch(data) {
    const response = await api.post("/subcontract-items/batch-create", data);
    return response?.data;
  },
  async updateItem(id, data) {
    const response = await api.put(`/subcontract-items/${id}`, data);
    return response?.data?.data ?? response?.data;
  },
  async updateCompletionPercentage(id, completionPercentage) {
    const response = await api.patch(`/subcontract-items/${id}/completion-percentage`, {
      completionPercentage,
    });
    return response?.data?.data ?? response?.data;
  },
  async deleteItem(id) {
    const response = await api.delete(`/subcontract-items/${id}`);
    return response?.data;
  },
  async getPaymentProfiles(id) {
    const profiles = unwrapList(await api.get(`/SubcontractPaymentProfile/subcontract/${id}`));
    return attachItems(profiles, async (profile) =>
      unwrapList(await api.get(`/SubcontractPaymentItem/payment-profile/${profile.paymentProfileId}`)),
    );
  },
  async createPaymentProfile(data) {
    const response = await api.post("/SubcontractPaymentProfile", data);
    return response?.data?.data ?? response?.data;
  },
  async updatePaymentProfile(id, data) {
    const response = await api.put(`/SubcontractPaymentProfile/${id}`, data);
    return response?.data?.data ?? response?.data;
  },
  async deletePaymentProfile(id) {
    const response = await api.delete(`/SubcontractPaymentProfile/${id}`);
    return response?.data;
  },
  async createPaymentItem(data) {
    const response = await api.post("/SubcontractPaymentItem", data);
    return response?.data?.data ?? response?.data;
  },
  async updatePaymentItem(id, data) {
    const response = await api.put(`/SubcontractPaymentItem/${id}`, data);
    return response?.data?.data ?? response?.data;
  },
  async deletePaymentItem(id) {
    const response = await api.delete(`/SubcontractPaymentItem/${id}`);
    return response?.data;
  },
  async getAcceptanceProfiles(id) {
    const profiles = unwrapList(await api.get(`/SubcontractAcceptanceProfile/subcontract/${id}`));
    return attachItems(profiles, async (profile) =>
      unwrapList(await api.get(`/SubcontractAcceptanceItem/acceptance-profile/${profile.acceptanceProfileId}`)),
    );
  },
  async getAcceptanceApprovalProfiles(params = {}) {
    const response = await api.get("/SubcontractAcceptanceApprovalProfile", { params });
    return {
      data: response?.data?.data ?? [],
      totalCount: response?.data?.totalCount ?? 0,
    };
  },
  async getAcceptanceApprovalProfilesBySubcontract(id) {
    const profiles = unwrapList(await api.get(`/SubcontractAcceptanceApprovalProfile/subcontract/${id}`));
    return attachItems(profiles, async (profile) =>
      unwrapList(await api.get(`/DrafSubcontractAcceptanceItem/approval-profile/${profile.acceptanceApprovalProfileId}`)),
    );
  },
  async getAcceptanceApprovalProfilesByAcceptanceProfile(id) {
    const profiles = unwrapList(await api.get(`/SubcontractAcceptanceApprovalProfile/acceptance-profile/${id}`));
    return attachItems(profiles, async (profile) =>
      unwrapList(await api.get(`/DrafSubcontractAcceptanceItem/approval-profile/${profile.acceptanceApprovalProfileId}`)),
    );
  },
  async createAcceptanceApprovalProfile(data) {
    const response = await api.post("/SubcontractAcceptanceApprovalProfile", data);
    return response?.data?.data ?? response?.data;
  },
  async updateAcceptanceApprovalProfile(id, data) {
    const statusText = String(data?.status || data?.Status || "").toLowerCase();
    const isStepDecision =
      Boolean(data?.respondedAt || data?.RespondedAt) &&
      (statusText.includes("duy") || statusText.includes("ph") || statusText.includes("sửa") || statusText.includes("sua"));

    if (isStepDecision) {
      const isRevision = statusText.includes("sửa") || statusText.includes("sua");
      const response = await api.post(`/SubcontractAcceptanceApprovalProfile/${id}/approve-step`, {
        action: isRevision ? "revision" : "approve",
        comment: data?.overallComment ?? data?.OverallComment ?? null,
      });
      return response?.data?.data ?? response?.data;
    }

    const response = await api.put(`/SubcontractAcceptanceApprovalProfile/${id}`, data);
    return response?.data?.data ?? response?.data;
  },
  async approveAcceptanceApprovalStep(id, data) {
    const response = await api.post(`/SubcontractAcceptanceApprovalProfile/${id}/approve-step`, data);
    return response?.data?.data ?? response?.data;
  },
  async reviewAcceptanceApprovalProfile(id, data) {
    const response = await api.post(`/SubcontractAcceptanceApprovalProfile/${id}/review`, data);
    return response?.data?.data ?? response?.data;
  },
  async deleteAcceptanceApprovalProfile(id) {
    const response = await api.delete(`/SubcontractAcceptanceApprovalProfile/${id}`);
    return response?.data;
  },
  async getDraftAcceptanceItems(id) {
    return sortItemsByCode(unwrap(await api.get(`/DrafSubcontractAcceptanceItem/acceptance-profile/${id}`)));
  },
  async getDraftAcceptanceItemsByApprovalProfile(id) {
    return sortItemsByCode(unwrap(await api.get(`/DrafSubcontractAcceptanceItem/approval-profile/${id}`)));
  },
  async initializeDraftAcceptanceItems(acceptanceProfileId, acceptanceApprovalProfileId = null) {
    const params = acceptanceApprovalProfileId ? { acceptanceApprovalProfileId } : undefined;
    const response = await api.post(`/DrafSubcontractAcceptanceItem/acceptance-profile/${acceptanceProfileId}/initialize`, null, { params });
    return response?.data;
  },
  async createDraftAcceptanceItem(data) {
    const response = await api.post("/DrafSubcontractAcceptanceItem", data);
    return response?.data?.data ?? response?.data;
  },
  async updateDraftAcceptanceItem(id, data) {
    const response = await api.put(`/DrafSubcontractAcceptanceItem/${id}`, data);
    return response?.data?.data ?? response?.data;
  },
  async deleteDraftAcceptanceItem(id) {
    const response = await api.delete(`/DrafSubcontractAcceptanceItem/${id}`);
    return response?.data;
  },
  async createAcceptanceProfile(data) {
    const response = await api.post("/SubcontractAcceptanceProfile", data);
    return response?.data?.data ?? response?.data;
  },
  async updateAcceptanceProfile(id, data) {
    const response = await api.put(`/SubcontractAcceptanceProfile/${id}`, data);
    return response?.data?.data ?? response?.data;
  },
  async deleteAcceptanceProfile(id) {
    const response = await api.delete(`/SubcontractAcceptanceProfile/${id}`);
    return response?.data;
  },
  async createAcceptanceItem(data) {
    const response = await api.post("/SubcontractAcceptanceItem", data);
    return response?.data?.data ?? response?.data;
  },
  async updateAcceptanceItem(id, data) {
    const response = await api.put(`/SubcontractAcceptanceItem/${id}`, data);
    return response?.data?.data ?? response?.data;
  },
  async deleteAcceptanceItem(id) {
    const response = await api.delete(`/SubcontractAcceptanceItem/${id}`);
    return response?.data;
  },
  async getContractHistory(id) {
    return unwrap(await api.get(`/SubcontractHistory/subcontract/${id}`, { params: { pageSize: 500 } }));
  },
  async getStatusHistory(id) {
    return unwrap(await api.get(`/Subcontract/${id}/status-history`, { params: { pageSize: 500 } }));
  },
  async getItemHistory(id) {
    return unwrap(await api.get(`/SubcontractItemHistory/subcontract/${id}`, { params: { pageSize: 500 } }));
  },
  async getApprovalHistory(id) {
    return unwrap(await api.get(`/SubcontractApprovalHistory/subcontract/${id}`));
  },
  async getAcceptanceApprovalHistory(id) {
    return unwrap(await api.get(`/SubcontractAcceptanceApprovalHistory/subcontract/${id}`));
  },
};

// Mock fallback cũ được giữ lại để có thể bật lại khi cần demo:
// export const safely = async (request, fallback) => {
//   try {
//     const value = await request();
//     if (Array.isArray(value) && value.length === 0) return fallback;
//     return value ?? fallback;
//   } catch {
//     return fallback;
//   }
// };
export const safely = async (request) => request();
